const FIRST_DIRTY_DELAY_MS = 60_000;
const RETRY_BASE_DELAYS_MS = [60_000, 300_000, 900_000, 3_600_000] as const;
const MAX_RETRY_DELAY_MS = 3_600_000;
const JITTER_FRACTION = 0.2;

export class HistoryBackupScheduler {
  private readonly upload: (snapshotRevision: number) => Promise<void>;
  private readonly setTimer: typeof setTimeout;
  private readonly clearTimer: typeof clearTimeout;
  private readonly random: () => number;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private dirty = false;
  private running = false;
  private revision = 0;
  private lastError: string | null = null;
  private retryIndex = 0;
  private stopped = false;

  constructor(options: {
    upload: (snapshotRevision: number) => Promise<void>;
    setTimer?: typeof setTimeout;
    clearTimer?: typeof clearTimeout;
    random?: () => number;
  }) {
    this.upload = options.upload;
    this.setTimer = options.setTimer ?? setTimeout;
    this.clearTimer = options.clearTimer ?? clearTimeout;
    this.random = options.random ?? Math.random;
  }

  markDirty(): number {
    this.revision += 1;
    if (!this.dirty) {
      this.dirty = true;
      this.schedule(FIRST_DIRTY_DELAY_MS);
    }
    return this.revision;
  }

  scheduleStartupBackfill(): void {
    if (this.dirty) return;
    this.dirty = true;
    this.schedule(this.jitteredDelay(FIRST_DIRTY_DELAY_MS));
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
  }

  get status(): { dirty: boolean; running: boolean; revision: number; lastError: string | null } {
    return {
      dirty: this.dirty,
      running: this.running,
      revision: this.revision,
      lastError: this.lastError,
    };
  }

  private async runUpload(): Promise<void> {
    this.timer = null;
    if (this.stopped) return;
    this.running = true;
    const snapshotRevision = this.revision;
    try {
      await this.upload(snapshotRevision);
      this.lastError = null;
      this.retryIndex = 0;
      if (snapshotRevision === this.revision) {
        this.dirty = false;
      } else {
        this.schedule(FIRST_DIRTY_DELAY_MS);
      }
    } catch (error) {
      this.lastError = sanitizeError(error);
      const baseDelay = RETRY_BASE_DELAYS_MS[
        Math.min(this.retryIndex, RETRY_BASE_DELAYS_MS.length - 1)
      ];
      this.retryIndex += 1;
      this.schedule(this.jitteredDelay(baseDelay));
    } finally {
      this.running = false;
    }
  }

  private schedule(delayMs: number): void {
    if (this.stopped) return;
    this.timer = this.setTimer(() => void this.runUpload(), delayMs);
  }

  private jitteredDelay(baseDelayMs: number): number {
    const sample = this.random();
    const boundedSample = Number.isFinite(sample) ? Math.min(1, Math.max(0, sample)) : 0.5;
    const multiplier = 1 - JITTER_FRACTION + 2 * JITTER_FRACTION * boundedSample;
    return Math.min(MAX_RETRY_DELAY_MS, Math.round(baseDelayMs * multiplier));
  }
}

function sanitizeError(error: unknown): string {
  let errorClass = "Error";
  let status: unknown;
  try {
    if (typeof error === "object" && error !== null) {
      const candidate = Object.getPrototypeOf(error)?.constructor?.name;
      if (typeof candidate === "string" && /^[A-Za-z_$][A-Za-z0-9_$]{0,63}$/.test(candidate)) {
        errorClass = candidate === "Object" ? "Error" : candidate;
      }
      status = Reflect.get(error, "status");
    }
  } catch {
    // Hostile errors and proxies still collapse to the generic class below.
  }
  return typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599
    ? `${errorClass} (status ${status})`
    : errorClass;
}
